import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import enTranslations from '../locales/en.json';
import rwTranslations from '../locales/rw.json';
import frTranslations from '../locales/fr.json';
import swTranslations from '../locales/sw.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultTranslations = {
  en: enTranslations,
  rw: rwTranslations,
  fr: frTranslations,
  sw: swTranslations
};

const LanguageContext = createContext(null);

// Cache key for localStorage
const TRANSLATION_CACHE_KEY = 'newsHubTranslationCache';
const CUSTOM_TRANSLATIONS_KEY = 'newsHubCustomTranslations';
const USER_LANGUAGE_KEY = 'yegoNewsLanguage';
const HAS_USER_PREFERENCE_KEY = 'yegoNewsHasLanguagePreference';

export const LanguageProvider = ({ children }) => {
  // Track if user has explicitly set their own preference
  const [hasUserPreference, setHasUserPreference] = useState(() => {
    return localStorage.getItem(HAS_USER_PREFERENCE_KEY) === 'true';
  });
  
  // Default language from admin (fetched from server)
  const [siteDefaultLanguage, setSiteDefaultLang] = useState('en');
  
  const [language, setLanguage] = useState(() => {
    // If user has set their own preference, use that
    if (localStorage.getItem(HAS_USER_PREFERENCE_KEY) === 'true') {
      const saved = localStorage.getItem(USER_LANGUAGE_KEY);
      if (saved) {
        document.documentElement.lang = saved;
        return saved;
      }
    }
    // Otherwise, will be set after fetching site default
    return 'en';
  });
  
  // Custom translations from admin (stored per language)
  const [customTranslations, setCustomTranslations] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOM_TRANSLATIONS_KEY);
      return saved ? JSON.parse(saved) : { en: {}, rw: {} };
    } catch {
      return { en: {}, rw: {} };
    }
  });
  
  // Load translation cache from localStorage
  const [translationCache, setTranslationCache] = useState(() => {
    try {
      const saved = localStorage.getItem(TRANSLATION_CACHE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Fetch site default language on mount
  useEffect(() => {
    const fetchSiteDefaultLanguage = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings/default-language`);
        const defaultLang = response.data.default_language || 'en';
        setSiteDefaultLang(defaultLang);
        
        // If user has no preference, apply the site default
        if (!hasUserPreference) {
          setLanguage(defaultLang);
          document.documentElement.lang = defaultLang;
        }
      } catch (error) {
        console.error('Failed to fetch site default language:', error);
      }
    };
    
    fetchSiteDefaultLanguage();
  }, [hasUserPreference]);

  // Fetch custom translations from server
  const fetchCustomTranslations = useCallback(async (lang) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/translations/custom?language=${lang}`);
      setCustomTranslations(prev => {
        const newCustom = { ...prev, [lang]: response.data };
        localStorage.setItem(CUSTOM_TRANSLATIONS_KEY, JSON.stringify(newCustom));
        return newCustom;
      });
    } catch (error) {
      console.error('Failed to fetch custom translations:', error);
    }
  }, []);

  useEffect(() => {
    // Update document lang attribute
    document.documentElement.lang = language;
    // Dispatch event for components that need to react
    window.dispatchEvent(new CustomEvent('yegoNewsLanguageChange', { detail: language }));
    
    // Fetch custom translations for current language
    fetchCustomTranslations(language);
  }, [language, fetchCustomTranslations]);

  // Save translation cache to localStorage when it changes
  useEffect(() => {
    try {
      // Limit cache size to prevent localStorage overflow
      const cacheKeys = Object.keys(translationCache);
      if (cacheKeys.length > 500) {
        // Keep only the most recent 300 entries
        const limitedCache = {};
        cacheKeys.slice(-300).forEach(key => {
          limitedCache[key] = translationCache[key];
        });
        localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(limitedCache));
      } else {
        localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(translationCache));
      }
    } catch (e) {
      console.warn('Failed to save translation cache:', e);
    }
  }, [translationCache]);

  // Get translation by key path (e.g., "menu.home")
  // First checks custom translations, then falls back to default
  const t = useCallback((keyPath, fallback = '') => {
    // First check custom translations
    const customValue = customTranslations[language]?.[keyPath];
    if (customValue) {
      return customValue;
    }
    
    // Fall back to default translations
    const keys = keyPath.split('.');
    let value = defaultTranslations[language];
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        return fallback || keyPath;
      }
    }
    
    return value || fallback || keyPath;
  }, [language, customTranslations]);

  // Toggle between EN and RW (user preference)
  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'rw' : 'en';
    setLanguage(newLang);
    setHasUserPreference(true);
    localStorage.setItem(USER_LANGUAGE_KEY, newLang);
    localStorage.setItem(HAS_USER_PREFERENCE_KEY, 'true');
  }, [language]);

  // Set specific language (user preference)
  const changeLanguage = useCallback((lang) => {
    const validLangs = ['en', 'rw', 'fr', 'sw'];
    if (validLangs.includes(lang)) {
      setLanguage(lang);
      setHasUserPreference(true);
      localStorage.setItem(USER_LANGUAGE_KEY, lang);
      localStorage.setItem(HAS_USER_PREFERENCE_KEY, 'true');
    }
  }, []);

  // Reset to site default (clear user preference)
  const resetToSiteDefault = useCallback(() => {
    setHasUserPreference(false);
    localStorage.removeItem(HAS_USER_PREFERENCE_KEY);
    localStorage.removeItem(USER_LANGUAGE_KEY);
    setLanguage(siteDefaultLanguage);
  }, [siteDefaultLanguage]);

  // Check if current language is Kinyarwanda
  const isKinyarwanda = language === 'rw';

  // Cache translated content (persisted to localStorage)
  const cacheTranslation = useCallback((key, translation) => {
    setTranslationCache(prev => ({
      ...prev,
      [key]: translation
    }));
  }, []);

  const getCachedTranslation = useCallback((key) => {
    return translationCache[key];
  }, [translationCache]);

  // Clear translation cache
  const clearTranslationCache = useCallback(() => {
    setTranslationCache({});
    localStorage.removeItem(TRANSLATION_CACHE_KEY);
  }, []);

  // Update a specific translation (used by admin editing)
  const updateTranslation = useCallback((key, value) => {
    setCustomTranslations(prev => {
      const newCustom = {
        ...prev,
        [language]: {
          ...prev[language],
          [key]: value
        }
      };
      localStorage.setItem(CUSTOM_TRANSLATIONS_KEY, JSON.stringify(newCustom));
      return newCustom;
    });
  }, [language]);

  // Refresh custom translations from server
  const refreshTranslations = useCallback(() => {
    fetchCustomTranslations(language);
  }, [language, fetchCustomTranslations]);

  // Translate text using the backend API
  const translateText = useCallback(async (text, targetLang = null) => {
    const target = targetLang || (language === 'en' ? 'rw' : 'en');
    const cacheKey = `${text}_${target}`;
    
    // Check cache first
    const cached = translationCache[cacheKey];
    if (cached) {
      return cached;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/translate`, {
        texts: [text],
        target_language: target
      });
      
      const translated = response.data.translations?.[0] || text;
      
      // Cache the result
      cacheTranslation(cacheKey, translated);
      
      return translated;
    } catch (error) {
      console.error('Translation failed:', error);
      return text; // Return original on error
    }
  }, [language, translationCache, cacheTranslation]);

  const value = {
    language,
    setLanguage: changeLanguage,
    toggleLanguage,
    t,
    isKinyarwanda,
    cacheTranslation,
    getCachedTranslation,
    clearTranslationCache,
    updateTranslation,
    refreshTranslations,
    translateText,
    customTranslations: customTranslations[language] || {},
    translations: defaultTranslations[language],
    // New: Site default language features
    siteDefaultLanguage,
    hasUserPreference,
    setSiteDefaultLanguage: async (lang) => {
      try {
        const token = localStorage.getItem('yegoNewsToken');
        await axios.post(`${BACKEND_URL}/api/settings/default-language`, 
          { language: lang },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        // Fetch the updated default
        const response = await axios.get(`${BACKEND_URL}/api/settings/default-language`);
        const newDefault = response.data.default_language || 'en';
        setSiteDefaultLang(newDefault);
        return { success: true };
      } catch (error) {
        console.error('Failed to set site default language:', error);
        return { success: false, error: error.response?.data?.detail || 'Failed to update' };
      }
    },
    resetToSiteDefault
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
